/**
 * Round / phase clock helpers.
 *
 * Preferred (API docs):
 *   remaining = max(0, phaseClockSecs - floor((now - phaseStartedAt).seconds))
 *
 * Fallback when phase fields missing: startedAt + default duration (115s live).
 */
export const DEFAULT_LIVE_PHASE_SECS = 115;
export const DEFAULT_BUY_PHASE_SECS = 20;
/** @deprecated use DEFAULT_LIVE_PHASE_SECS */
export const DEFAULT_ROUND_DURATION_SECS = DEFAULT_LIVE_PHASE_SECS;

export type PhaseClockSource = {
  status?: string | null;
  startedAt?: string | null;
  phaseClockSecs?: number | null;
  phaseStartedAt?: string | null;
};

/** Remaining countdown seconds from phase fields, or null when unknown. */
export function getPhaseRemainingSecs(
  round: PhaseClockSource | null | undefined,
  nowMs: number,
): number | null {
  if (!round) return null;

  const phaseStart = round.phaseStartedAt ?? null;
  const phaseDur = round.phaseClockSecs;
  if (phaseStart && phaseDur != null && phaseDur > 0) {
    return remainingFrom(phaseStart, nowMs, phaseDur);
  }

  // Fallback for older payloads
  if (round.startedAt) {
    const fallbackDur =
      round.status === 'buy' ? DEFAULT_BUY_PHASE_SECS : DEFAULT_LIVE_PHASE_SECS;
    return remainingFrom(round.startedAt, nowMs, fallbackDur);
  }

  return null;
}

/** Remaining countdown seconds, or null when startedAt is missing/invalid. */
export function getRemainingRoundSecs(
  startedAt: string | null | undefined,
  nowMs: number,
  durationSecs: number = DEFAULT_LIVE_PHASE_SECS,
): number | null {
  if (!startedAt) return null;
  return remainingFrom(startedAt, nowMs, durationSecs);
}

function remainingFrom(startedAt: string, nowMs: number, durationSecs: number): number | null {
  const startedMs = Date.parse(startedAt);
  if (Number.isNaN(startedMs)) return null;
  const elapsed = Math.floor((nowMs - startedMs) / 1000);
  return Math.max(0, durationSecs - elapsed);
}

/** Formats non-negative seconds as m:ss (e.g. 83 → "1:23"). */
export function formatClock(secs: number): string {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r < 10 ? '0' : ''}${r}`;
}

export function defaultPhaseSecsForStatus(status: string | null | undefined): number {
  return status === 'buy' ? DEFAULT_BUY_PHASE_SECS : DEFAULT_LIVE_PHASE_SECS;
}
